import sanitizeHtml from 'sanitize-html'
import type { Directive } from 'vue'

/**
 * Sanitizes CMS-authored rich text. The library's defaults already block
 * dangerous content (scripts, event handlers, javascript: URLs); we just widen
 * the allow-list to include media, SVG, inline styles, and theming attributes
 * that the defaults strip but our CMS blocks rely on.
 */
const options: sanitizeHtml.IOptions = {
  allowedTags: [
    ...sanitizeHtml.defaults.allowedTags,
    'img',
    'picture',
    'source',
    'video',
    'audio',
    'del',
    'ins',
    'svg',
    'g',
    'path',
    'circle',
    'rect',
    'line',
    'polyline',
    'polygon',
    'ellipse',
    'defs',
    'use',
    'symbol',
    'title',
    'desc',
  ],
  allowedAttributes: {
    '*': ['class', 'id', 'style', 'role', 'lang', 'dir', 'tabindex', 'data-*', 'aria-*'],
    a: ['href', 'name', 'target', 'rel', 'download', 'hreflang', 'type'],
    img: ['src', 'srcset', 'sizes', 'alt', 'title', 'width', 'height', 'loading', 'decoding'],
    source: ['src', 'srcset', 'sizes', 'media', 'type'],
    video: ['src', 'poster', 'controls', 'autoplay', 'loop', 'muted', 'playsinline', 'preload', 'width', 'height'],
    audio: ['src', 'controls', 'autoplay', 'loop', 'muted', 'preload'],
    svg: ['xmlns', 'viewBox', 'width', 'height', 'fill', 'stroke', 'preserveAspectRatio'],
    g: ['fill', 'stroke', 'transform', 'opacity'],
    path: ['d', 'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'opacity'],
    circle: ['cx', 'cy', 'r', 'fill', 'stroke', 'stroke-width'],
    rect: ['x', 'y', 'width', 'height', 'rx', 'ry', 'fill', 'stroke', 'stroke-width'],
    line: ['x1', 'y1', 'x2', 'y2', 'stroke', 'stroke-width'],
    polyline: ['points', 'fill', 'stroke', 'stroke-width'],
    polygon: ['points', 'fill', 'stroke', 'stroke-width'],
    ellipse: ['cx', 'cy', 'rx', 'ry', 'fill', 'stroke', 'stroke-width'],
    use: ['href', 'xlink:href', 'x', 'y', 'width', 'height'],
    symbol: ['viewBox'],
  },
  allowedSchemes: ['http', 'https', 'mailto', 'tel'],
  allowedSchemesByTag: {},
  allowProtocolRelative: true,
  allowedSchemesAppliedToAttributes: ['href', 'src', 'cite'],
}

function sanitizeHtmlContent(html: string): string {
  return sanitizeHtml(html, options)
}

const sanitize: Directive = (el, binding) => {
  el.innerHTML = sanitizeHtmlContent(binding.value)
}

export default sanitize
